import type { TaskSummary } from './tasks';
import type { TaskGrouping } from './taskGrouping';
import { TASK_STATUSES, type TaskStatus } from './taskStatus';

/** One block of rows under one heading. `status: null` is the ungrouped list,
 *  which has no heading at all. */
export type TaskGroup = { status: TaskStatus | null; tasks: TaskSummary[] };

/**
 * Due date decides the list, here rather than at the fetch. Exported for the
 * dashboard's day card, which is ordered the same way for the same reason.
 *
 * Ordering on the way to the screen instead of trusting the order the rows
 * arrived in is what makes an edited due date move its row in the same paint.
 * The server's order is kept as the tiebreak -- so the sort control is the
 * secondary order its labels say it is ("Then by …"), deciding the undated tail
 * and the odd shared due date.
 *
 * Undated work sorts last, not first: a null dueDate means "no deadline", not
 * "overdue since the epoch". Same rule the server's `nulls: 'last'` states.
 *
 * `sort` is stable in every engine this runs in, which is what leaves the
 * server's order intact within a due date rather than reshuffling it.
 */
export function orderByDueDate(tasks: TaskSummary[]): TaskSummary[] {
  return [...tasks].sort((a, b) => {
    if (a.dueDate === b.dueDate) return 0;
    if (a.dueDate === null) return 1;
    if (b.dueDate === null) return -1;
    // 'YYYY-MM-DD' compares correctly as a string, so no Date is constructed.
    return a.dueDate < b.dueDate ? -1 : 1;
  });
}

/**
 * The list's reading order: status groups in the explicit TASK_STATUSES order,
 * and inside each one by due date. Ungrouped, it is one block ordered the same
 * way.
 *
 * Shared rather than left inside TaskList because shift-click needs to know
 * which rows sit "between" two clicks, and that answer has to be the order on
 * screen. Two copies of this loop would eventually disagree, and the way you
 * would find out is a shift-click selecting the wrong rows.
 *
 * That is also why the grouping toggle goes through here rather than being a
 * branch inside the component: the moment the rendered order can change, the
 * order shift-click measures against has to change with it, from the same call.
 */
export function groupTasks(tasks: TaskSummary[], grouping: TaskGrouping): TaskGroup[] {
  if (grouping === 'none') return [{ status: null, tasks: orderByDueDate(tasks) }];

  return TASK_STATUSES.map((status) => ({
    status,
    tasks: orderByDueDate(tasks.filter((task) => task.status === status)),
  }));
}

/** The same order, flattened -- one index per visible row. */
export function orderTasks(tasks: TaskSummary[], grouping: TaskGrouping): TaskSummary[] {
  return groupTasks(tasks, grouping).flatMap((group) => group.tasks);
}
